import { useMemo } from 'react';
import { css } from 'styled-components';

import {
  DropdownFilterChipRoot,
  DropdownFilterChip,
  DropdownFilterChipMenu,
  DropdownFilterChipCheckboxMenuItem,
} from './DropdownFilterChip';
import { Stack } from '../Layout';

import type { ChipSize } from './Chip';

export type FilterChipItem<Value extends string> = {
  label: string;
  value: Value;
  count?: number;
};

type MultiSelectFilterChipProps<Value extends string> = {
  size?: ChipSize;
  group: string;
  placeholder: string;
  selected: ReadonlyArray<string>;
  items: ReadonlyArray<FilterChipItem<Value>>;
  onChange: (value: Value, checked: boolean) => void;
  onClear?: () => void;
  css?: string;
  name?: string;
  disabled?: boolean;
};

export function MultiSelectFilterChip<Value extends string>(
  props: MultiSelectFilterChipProps<Value>
): React.ReactElement {
  const {
    size = 'md',
    disabled = false,
    group,
    placeholder,
    onClear,
    onChange,
    selected,
    items = [],
    name,
    ...rest
  } = props;
  const itemValues = useMemo(() => {
    return items.reduce((map: Map<Value | string, string>, item) => {
      map.set(item.value, item.label);
      return map;
    }, new Map());
  }, [items]);
  const hasSelection = selected.length > 0;
  const firstSelectedLabel = hasSelection ? itemValues.get(selected[0]) : undefined;
  const textContent =
    selected.length > 1 && firstSelectedLabel !== undefined
      ? `${firstSelectedLabel} (+${selected.length - 1})`
      : hasSelection && firstSelectedLabel !== undefined
        ? firstSelectedLabel
        : placeholder;

  return (
    <DropdownFilterChipRoot>
      <DropdownFilterChip
        {...rest}
        data-analytics-name={`multi-select-filter-${name ?? placeholder.toLowerCase().replace(' ', '_')}`}
        data-testid={`multi-select-filter-${name ?? placeholder.toLowerCase().replace(' ', '_')}`}
        size={size}
        hasSelection={hasSelection}
        disabled={disabled}
        onClear={onClear}
      >
        {textContent}
      </DropdownFilterChip>
      <DropdownFilterChipMenu>
        <Stack
          vertical
          css={css`
            padding-right: 2rem;
          `}
        >
          {items.map(({ label, value }) => (
            <DropdownFilterChipCheckboxMenuItem
              key={value}
              label={label}
              value={value}
              group={group}
              checked={props.selected.some((s) => s === value)}
              onChange={(checked) => onChange(value, checked)}
            />
          ))}
        </Stack>
      </DropdownFilterChipMenu>
    </DropdownFilterChipRoot>
  );
}
